import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  AccountHolderMode,
  CurrencyEnum,
  IAddFeeTypeRequest,
  IExchangeRateResponse,
  IFeeTypesResponse,
  IFeeTypesState,
  ILoginRequest,
  ILoginResponse,
  IResponseSummary,
  ISchools,
  IStudentSummary,
  ISuccessResponse,
  IUpdateFeeRequest,
  IUpdateStudentsStatus,
  UserRole,
} from './types';
import {
  IRegisterUserRequest,
  IRegisterUserResponse,
  IUsersAll,
  IUsersAllSummary,
} from './models/auth';
import {
  FeeType,
  IDeleteStudentsRequest,
  IStudent,
  IStudentsFetchAllByLevelRequest,
  IStudentsFetchAllRequest,
  IStudentTableView,
} from './models/students';
import { ICreateSchoolRequestDto, ICreateSchoolResponseDto } from './models/schools';
import {
  ICashReportSummary,
  IComparePeriodsResponse,
  IExpensesReport,
  IExpensesReportResponse,
  IMonthlyReportProps,
  ITransactionForCashReportResponse,
  ITransactionForUserReport,
} from './models/transactions';
import dayjs from 'dayjs';
import { ISchoolTerm, SchoolTermEnum } from './models/schoolTerms';
import { RootState } from '../store';

import { KeyedObject } from '../types/root';
import jwtDecode from 'jwt-decode';
import { IPaymentHistory } from './models/fees';
import { ISendMessageRequest, ISendMessageResponse, ISentMessage } from './models/messaging';
import {
  ICalendarEvent,
  ICalendarEventsQuery,
  ICreateCalendarEventRequest,
  IUpdateCalendarEventRequest,
} from './models/calendar';

export const ACCESS_TOKEN = 'access_token';
export const TENANT_ID = 'tenantId';

const TENANT_ID_KEY = 'x-tenant-id';
const USER_ROLE_HEADER_KEY = 'x-user-role';
const ACCESS_TOKEN_KEY = 'x-access-token';
const excludeUrls = ['login'];

export const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }
  const decoded: KeyedObject = jwtDecode(serviceToken);

  /**
   * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
   */
  return decoded.exp > Date.now() / 1000;
};
const getTokenFromStorage = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (token && verifyToken(token)) {
    return token;
  }
  return null;
};
const getTenantIdFromStorage = () => {
  const tenantId = localStorage.getItem(TENANT_ID);
  if (tenantId) {
    return tenantId;
  }
  return null;
};

export const konektApi = createApi({
  reducerPath: 'konektApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_HOST_URL,
    prepareHeaders: (headers, { getState, endpoint }) => {
      const accessToken = (getState() as RootState).login.access_token || getTokenFromStorage();
      const tenantId = (getState() as RootState).login.tenantId || getTenantIdFromStorage();

      if (excludeUrls.includes(endpoint)) {
        return headers;
      }

      // If we have a token set in state, let's assume that we should be passing it.
      if (tenantId) {
        headers.set(TENANT_ID_KEY, tenantId);
      }
      if (accessToken) {
        headers.set(ACCESS_TOKEN_KEY, accessToken);
      }
      return headers;
    },
  }),
  tagTypes: [
    'Students',
    'ExchangeRate',
    'Login',
    'Users',
    'FeeTypes',
    'SchoolTerms',
    'Messages',
    'CalendarEvents',
  ],
  endpoints: (builder) => ({
    login: builder.mutation<ILoginResponse, ILoginRequest>({
      query: (body: ILoginRequest) => ({
        url: '/v1/auth/login',
        method: 'POST',
        headers: {
          // Cashiers are also allowed; the backend still requires CAN_USE_WEB for them.
          [USER_ROLE_HEADER_KEY]: [UserRole.ADMIN, UserRole.CASHIER].join(','),
        },
        body,
      }),
      invalidatesTags: ['Students', 'ExchangeRate', 'Login'],
    }),
    getFeesMonthly: builder.query<IResponseSummary, { dateFrom: string }>({
      query: (queryArgs) => `/v1/reports/summary/${queryArgs.dateFrom}`,
      providesTags: ['Students'],
    }),
    getExchangeRate: builder.query<IExchangeRateResponse, void>({
      query: (queryArgs) => `/v1/fees/exchangeRate`,
    }),
    getLoggedInUser: builder.query<ILoginResponse, void>({
      query: (queryArgs) => `/v1/auth/loggedInUser`,
    }),
    updateExchangeRate: builder.mutation<
      IResponseSummary,
      { rate: number; currency: CurrencyEnum }
    >({
      query: (queryArgs) => {
        return {
          url: `/v1/fees/exchangeRate/${queryArgs.rate}/currency/${queryArgs.currency}`,
          method: 'PUT',
        };
      },
      invalidatesTags: ['ExchangeRate'],
    }),
    getAllUsers: builder.query<IUsersAllSummary[], void>({
      query: (queryArgs) => `/v1/auth/getAllUsers`,
      transformResponse(response: IUsersAll[]) {
        return response.map((user) => {
          return {
            ...user,
            id: user._id,
            names: `${user.lastName} ${user.firstName || ''}`,
          } as IUsersAllSummary;
        });
      },
    }),
    getClientFeeType: builder.query<IFeeTypesState, void>({
      query: () => `/v1/fees/getClientFeeType`,
      providesTags: ['FeeTypes'],
      transformResponse(feeTypes: IFeeTypesResponse) {
        return {
          ...feeTypes,
          other: feeTypes.other.map((item) => item.sub_category || item).flat(),
          accountHolderMode: feeTypes.accountHolderMode || AccountHolderMode.PARENT,
        } as IFeeTypesState;
      },
    }),
    getUserById: builder.query<IUsersAllSummary, { id: string }>({
      query: (queryArgs) => `/v1/auth/getUserById/${queryArgs.id}`,
      transformResponse(user: IUsersAll & { lastLogin?: string }) {
        return {
          ...user,
          id: user._id,
          names: `${user.lastName} ${user.firstName || ''}`,
          lastLogin: user.lastLogin,
        } as IUsersAllSummary;
      },
    }),
    getAllExpensesSummary: builder.query<IExpensesReport[], { dateFrom: string }>({
      query: (queryArgs) => `/v1/reports/expenses/${queryArgs.dateFrom}`,
      transformResponse(expenses: IExpensesReportResponse[]) {
        return expenses.map((item) => {
          return {
            ...item,
            id: item._id,
            cashier: item.cashier[0].firstName + ' ' + item.cashier[0].lastName,
            authorizer: item.authorizer[0].firstName + ' ' + item.authorizer[0].lastName,
            to: item.beneficiary_name,
            date: dayjs(item.date).format('MMM D, YYYY HH:mm'),
            description: item.description ? item.description : '',
          };
        });
      },
    }),
    getCashReportForPeriod: builder.query<
      ICashReportSummary[],
      { dateFrom: string; dateTo: string }
    >({
      query: (queryArgs) =>
        `/v1/reports/cashReportForPeriod/${queryArgs.dateFrom}/${queryArgs.dateTo}`,
    }),
    getTransactionForUser: builder.query<
      ITransactionForUserReport,
      { date: string; userId: string }
    >({
      query: (queryArgs) => `/v1/reports/transactions/${queryArgs.userId}/${queryArgs.date}`,
      transformResponse(transactionForUserReport: ITransactionForUserReport) {
        return {
          expenses: transactionForUserReport.expenses.map((expense) => {
            return {
              ...expense,
              id: expense._id,
              date: dayjs(expense.date).format('D MMM, HH:mm'),
            };
          }),
          incomes: transactionForUserReport.incomes.map((income) => {
            const classroomSplit = income.classroomDisplayName.split(' ');

            return {
              ...income,
              id: income._id,
              date_sold: dayjs(income.date_sold).format('D MMM, HH:mm'),
              classroom: classroomSplit[0] + ' ' + classroomSplit[1],
            };
          }),
        };
      },
    }),
    getTransactionsReportForPeriod: builder.query<
      ITransactionForCashReportResponse,
      { startDate: string; endDate: string }
    >({
      query: (queryArgs) =>
        `/v1/reports/balanceForPeriod/${queryArgs.startDate}/${queryArgs.endDate}`,
      transformResponse(transactionForCashReport: ITransactionForCashReportResponse) {
        return {
          expenses: transactionForCashReport.expenses.map((expense) => {
            return {
              ...expense,
              id: expense._id,
              date: dayjs(expense.date).format('D MMM, HH:mm'),
            };
          }),
          incomes: transactionForCashReport.incomes.map((income) => {
            return {
              ...income,
              id: income._id,
              date_sold: dayjs(income.date_sold).format('D MMM, HH:mm'),
            };
          }),
        };
      },
    }),
    getReportSummaryForPeriod: builder.query<
      IMonthlyReportProps,
      { startDate: string; endDate: string }
    >({
      query: (queryArgs) =>
        `/v1/reports/summary-for-period/${queryArgs.startDate}/${queryArgs.endDate}`,
    }),
    getComparePeriods: builder.query<
      IComparePeriodsResponse,
      { periodAStart: string; periodAEnd: string; periodBStart: string; periodBEnd: string }
    >({
      query: (queryArgs) =>
        `/v1/reports/compare-periods/${queryArgs.periodAStart}/${queryArgs.periodAEnd}/${queryArgs.periodBStart}/${queryArgs.periodBEnd}`,
    }),
    getByStudentNumber: builder.query<IStudent, { studentNumber: string }>({
      query: (queryArgs) => `v1/schools/findStudent?studentNumber=${queryArgs.studentNumber}`,
      transformResponse(student: IStudent) {
        let credit = 0;
        student.paymentStatus = student.paymentStatus.map((item) => {
          if (item.feeType === student.yearlySchoolFeeType && item.feeType !== FeeType.YEARLY_FEE) {
            credit = item.credit;
            return {
              ...item,
              feeType: FeeType.YEARLY_FEE,
              originalFeeType: item.feeType,
            };
          }
          return { ...item, originalFeeType: item.feeType };
        });
        student.credit = credit;
        return student;
      },
      providesTags: ['Students'],
    }),
    uploadStudents: builder.mutation<ISuccessResponse, FormData>({
      query: (formData: FormData) => {
        return {
          url: '/v1/students/uploadStudents',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Students'],
    }),
    updateStudentsStatus: builder.mutation<ISuccessResponse, IUpdateStudentsStatus>({
      query: (queryArgs) => {
        return {
          url: `/v1/students/setStatusByFee/?minFee=${queryArgs.minFee}&status=${queryArgs.status}&penalty=${queryArgs.penalty}`,
          method: 'PUT',
        };
      },
      invalidatesTags: ['Students'],
    }),
    updateStudentDetails: builder.mutation<ISuccessResponse, Partial<IStudentSummary>>({
      query: (body) => {
        return {
          url: `/v1/students/details`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['Students'],
    }),
    updateStudentFees: builder.mutation<ISuccessResponse, IUpdateFeeRequest>({
      query: (body) => {
        return {
          url: `/v1/students/fees`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: ['Students'],
    }),
    deleteStudents: builder.mutation<ISuccessResponse, IDeleteStudentsRequest>({
      query: (body) => {
        return {
          url: `/v1/students`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: ['Students'],
    }),
    registerUser: builder.mutation<IRegisterUserResponse, IRegisterUserRequest>({
      query: (body: IRegisterUserRequest) => {
        return {
          url: '/v1/auth/register',
          method: 'POST',
          body: { ...body, branchCode: 'N/A' },
        };
      },
    }),
    addSchool: builder.mutation<ICreateSchoolResponseDto, ICreateSchoolRequestDto>({
      query: (body: ICreateSchoolRequestDto) => {
        return {
          url: '/v1/schools/create',
          method: 'POST',
          body: { ...body },
        };
      },
    }),
    addSchoolTerms: builder.mutation<ISchoolTerm[], ISchoolTerm[]>({
      query: (terms: ISchoolTerm[]) => {
        return {
          url: '/v1/schools/terms',
          method: 'POST',
          body: terms,
        };
      },
      invalidatesTags: ['SchoolTerms'],
    }),
    makeTermCurrent: builder.mutation<ISuccessResponse, { term: string }>({
      query: ({ term }) => {
        return {
          url: `/v1/schools/terms/${encodeURIComponent(term)}/make-current`,
          method: 'PATCH',
        };
      },
      invalidatesTags: ['SchoolTerms'],
    }),
    updateTermStatus: builder.mutation<ISuccessResponse, { term: string; status: SchoolTermEnum }>({
      query: ({ term, status }) => {
        return {
          url: `/v1/schools/terms/${encodeURIComponent(term)}/status`,
          method: 'PATCH',
          body: { status },
        };
      },
      invalidatesTags: ['SchoolTerms'],
    }),
    getAllStudents: builder.query<IStudentTableView[], IStudentsFetchAllRequest>({
      query: (queryArgs) => {
        return `/v1/students/findAll/limit/${queryArgs.limit}/page/${queryArgs.page}`;
      },
      providesTags: ['Students'],
      transformResponse(response: IStudentSummary[]) {
        return response.map((std) => {
          return {
            ...std,
            fee_paid: std.credit,
            id: std.studentNumber,
            names: `${std.surname} ${std.middleName || ''} ${std.firstname || ''}`,
          } as IStudentTableView;
        });
      },
    }),
    getStudentPaymentHistory: builder.query<
      IPaymentHistory[],
      { studentNumber: string; studyYear: string }
    >({
      query: (queryArgs) => {
        return `/v1/fees/getPaymentHistory/${queryArgs.studentNumber}/${queryArgs.studyYear}`;
      },
      transformResponse(response: IPaymentHistory[]) {
        return response.map((payment) => {
          return {
            ...payment,
            id: payment._id,
          };
        });
      },
    }),
    getSchoolTerms: builder.query<ISchoolTerm[], void>({
      query: () => {
        return `/v1/schools/terms`;
      },
      providesTags: ['SchoolTerms'],
    }),
    getAllStudentsByLevel: builder.query<IStudentTableView[], IStudentsFetchAllByLevelRequest>({
      query: (queryArgs) => {
        return `/v1/students/findAllByLevel?level=${queryArgs.level}&pattern=${queryArgs.pattern}&term=${queryArgs?.term}`;
      },
      providesTags: ['Students'],
      transformResponse(response: IStudentSummary[]) {
        return response.map((std) => {
          return {
            ...std,
            fee_paid: std.credit,
            id: std.studentNumber,
            names: `${std.surname} ${std.middleName || ''} ${std.firstname || ''}`,
          } as IStudentTableView;
        });
      },
    }),
    reportStudents: builder.mutation<ISuccessResponse, FormData>({
      query: (formData: FormData) => {
        return {
          url: '/v1/students/uploadStudents',
          method: 'POST',
          body: formData,
        };
      },
    }),
    getAllSchools: builder.query<ISchools[], void>({
      query: () => `/v1/schools`,
    }),
    staffForgotPassword: builder.mutation<
      { message: string },
      { username: string; companyName: string }
    >({
      query: ({ companyName, ...body }) => ({
        url: '/v1/auth/forgot-password',
        method: 'POST',
        headers: { [TENANT_ID_KEY]: companyName },
        body: { ...body, companyName },
      }),
    }),
    staffResetPassword: builder.mutation<
      { message: string },
      { token: string; newPassword: string; companyName: string }
    >({
      query: ({ companyName, ...body }) => ({
        url: '/v1/auth/reset-password',
        method: 'POST',
        headers: { [TENANT_ID_KEY]: companyName },
        body,
      }),
    }),
    // Parent forgot hits the identity endpoint — looks the parent up by email
    // only (globally unique), so no tenant/companyName is required.
    parentForgotPassword: builder.mutation<{ message: string }, { email: string }>({
      query: (body) => ({
        url: '/v1/parent/forgot-password',
        method: 'POST',
        body,
      }),
    }),
    // Parent reset hits a separate endpoint — the staff one rejects parent
    // tokens (role: { $ne: PARENT }). companyName goes in the body (the parent
    // endpoint requires it to select the tenant DB and scope the token lookup).
    parentResetPassword: builder.mutation<
      { message: string },
      { token: string; newPassword: string; companyName: string }
    >({
      query: ({ companyName, ...rest }) => ({
        url: '/v1/parent/reset-password',
        method: 'POST',
        headers: { [TENANT_ID_KEY]: companyName },
        body: { ...rest, companyName },
      }),
    }),
    updateUser: builder.mutation<ISuccessResponse, { id: string } & Partial<IRegisterUserRequest>>({
      query: ({ id, ...body }) => ({
        url: `/v1/auth/updateUser/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    adminResetPassword: builder.mutation<ISuccessResponse, { userId: string }>({
      query: ({ userId }) => ({
        url: `/v1/auth/resetPasswordAdmin`,
        method: 'POST',
        body: { userId },
      }),
    }),
    deactivateUser: builder.mutation<ISuccessResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/auth/deactivateUser/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Users'],
    }),
    reactivateUser: builder.mutation<ISuccessResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/auth/reactivateUser/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Users'],
    }),
    addFeeType: builder.mutation<ISuccessResponse, IAddFeeTypeRequest>({
      query: (body) => ({
        url: '/v1/fees/addFeeType',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FeeTypes'],
    }),
    // --- Parent messaging (Phase 2, contract §11.8) ---
    sendParentMessage: builder.mutation<ISendMessageResponse, ISendMessageRequest>({
      query: (body) => ({
        url: '/v1/staff/messages',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Messages'],
    }),
    getStudentSentMessages: builder.query<ISentMessage[], { studentNumber: string }>({
      query: ({ studentNumber }) =>
        `/v1/staff/messages?studentNumber=${encodeURIComponent(studentNumber)}`,
      providesTags: ['Messages'],
    }),
    // --- School calendar (Phase 2, contract §11.8) ---
    getCalendarEvents: builder.query<ICalendarEvent[], ICalendarEventsQuery | void>({
      query: (queryArgs) => {
        const params = new URLSearchParams();
        if (queryArgs?.from) params.set('from', queryArgs.from);
        if (queryArgs?.to) params.set('to', queryArgs.to);
        const qs = params.toString();
        return `/v1/staff/calendar/events${qs ? `?${qs}` : ''}`;
      },
      providesTags: ['CalendarEvents'],
    }),
    createCalendarEvent: builder.mutation<ICalendarEvent, ICreateCalendarEventRequest>({
      query: (body) => ({
        url: '/v1/staff/calendar/events',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CalendarEvents'],
    }),
    updateCalendarEvent: builder.mutation<ICalendarEvent, IUpdateCalendarEventRequest>({
      query: ({ id, ...body }) => ({
        url: `/v1/staff/calendar/events/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CalendarEvents'],
    }),
    deleteCalendarEvent: builder.mutation<{ deleted: boolean }, { id: string }>({
      query: ({ id }) => ({
        url: `/v1/staff/calendar/events/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CalendarEvents'],
    }),
  }),
});

export const {
  useGetUserByIdQuery,
  useGetFeesMonthlyQuery,
  useLazyGetFeesMonthlyQuery,
  useLoginMutation,
  useGetAllSchoolsQuery,
  useGetAllUsersQuery,
  useUploadStudentsMutation,
  useGetAllStudentsQuery,
  useRegisterUserMutation,
  useLazyGetByStudentNumberQuery,
  useGetByStudentNumberQuery,
  useUpdateStudentsStatusMutation,
  useAddSchoolMutation,
  useUpdateStudentFeesMutation,
  useUpdateStudentDetailsMutation,
  useDeleteStudentsMutation,
  useLazyGetAllStudentsByLevelQuery,
  useLazyGetAllExpensesSummaryQuery,
  useLazyGetTransactionForUserQuery,
  useLazyGetStudentPaymentHistoryQuery,
  useGetClientFeeTypeQuery,
  useLazyGetTransactionsReportForPeriodQuery,
  useLazyGetCashReportForPeriodQuery,
  useLazyGetExchangeRateQuery,
  useUpdateExchangeRateMutation,
  useGetSchoolTermsQuery,
  useAddSchoolTermsMutation,
  useMakeTermCurrentMutation,
  useUpdateTermStatusMutation,
  useGetLoggedInUserQuery,
  useLazyGetLoggedInUserQuery,
  useLazyGetReportSummaryForPeriodQuery,
  useLazyGetComparePeriodsQuery,
  useStaffForgotPasswordMutation,
  useStaffResetPasswordMutation,
  useParentForgotPasswordMutation,
  useParentResetPasswordMutation,
  useUpdateUserMutation,
  useAdminResetPasswordMutation,
  useDeactivateUserMutation,
  useReactivateUserMutation,
  useAddFeeTypeMutation,
  useSendParentMessageMutation,
  useLazyGetStudentSentMessagesQuery,
  useGetCalendarEventsQuery,
  useCreateCalendarEventMutation,
  useUpdateCalendarEventMutation,
  useDeleteCalendarEventMutation,
} = konektApi;
